import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import senderRequests from "../../../../api/Customer/SenderRequests";
import CustomerLayout from "../../../../layouts/CustomerLayout";
import Loader from "../../../../components/utilities/Loader";

//Todo => Import the new components
import {
  SenderHeader,
  StatsCards,
  SenderManagementContent,
  HelpSection,
} from "../../../../components/senders";

// Import modal components
import AddSenderModal from "../../../../components/senders/AddSenderModal";
import DeleteConfirmModal from "../../../../components/senders/DeleteConfirmModal";

const Senders = () => {
  const [senderIds, setSenderIds] = useState([]);
  const [newSenderId, setNewSenderId] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTips, setShowTips] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingSender, setIsAddingSender] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [senderToDelete, setSenderToDelete] = useState(null);
  const [isDeletingSender, setIsDeletingSender] = useState(false);
  const [isEditingSender, setIsEditingSender] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const searchTimeoutRef = useRef(null);
  const [statsData, setStatsData] = useState({
    total_sender_ids: 0,
    total_approved: 0,
    total_pending: 0,
    total_rejected: 0
  });

  // Pagination state (server-side)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 9,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle search with debounce
  const handleSearchChange = (value) => {
    setSearchInput(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1); // Reset to first page on search
    }, 2000);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchSenderIds = async () => {
      setIsLoading(true);
      try {
        const response = await senderRequests.getSenders(
          currentPage,
          pagination.perPage,
          searchQuery
        );
        const { data } = response;
        
        if (data?.data?.status_code === "000" && !data?.data?.in_error) {
          // Extract data from the nested structure
          const apiData = data?.data?.data;
          const senderData = apiData?.data || [];
          const paginationData = apiData?.pagination || {};
          const stats = apiData?.sender_id_stats || {
            total_sender_ids: 0,
            total_approved: 0,
            total_pending: 0,
            total_rejected: 0
          };

          setSenderIds(Array.isArray(senderData) ? senderData : []);
          setStatsData(stats);
          setPagination({
            currentPage: paginationData.current_page || currentPage,
            perPage: paginationData.per_page || pagination.perPage,
            total: paginationData.total || 0,
            lastPage: paginationData.last_page || 1,
            from: paginationData.from || 0,
            to: paginationData.to || 0,
          });
        } else {
          setSenderIds([]);
          setStatsData({
            total_sender_ids: 0,
            total_approved: 0,
            total_pending: 0,
            total_rejected: 0
          });
          setPagination({
            currentPage: 1,
            perPage: pagination.perPage,
            total: 0,
            lastPage: 1,
            from: 0,
            to: 0,
          });
          toast.error(
            data?.data?.reason || "An error occurred while fetching sender IDs"
          );
        }
      } catch (error) {
        console.error('Error fetching sender IDs:', error);
        setSenderIds([]);
        setStatsData({
          total_sender_ids: 0,
          total_approved: 0,
          total_pending: 0,
          total_rejected: 0
        });
        setPagination({
          currentPage: 1,
          perPage: pagination.perPage,
          total: 0,
          lastPage: 1,
          from: 0,
          to: 0,
        });
        toast.error(
          error?.response?.data?.reason ||
            "An error occurred while fetching sender IDs"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchSenderIds();
  }, [currentPage, searchQuery, pagination.perPage]);

  //Todo => Validation function
  const validateSenderId = (value, excludeSlug = null) => {
    const errors = {};

    if (!value.trim()) {
      errors.empty = "Sender ID cannot be empty";
      return errors;
    }

    if (value.length > 11) {
      errors.length = "Sender ID cannot exceed 11 characters";
    }

    //Todo=> Check for special characters (allow only letters, numbers, and spaces)
    const specialCharRegex = /[^a-zA-Z0-9\s]/;
    if (specialCharRegex.test(value)) {
      errors.special = "Special characters are not allowed";
    }

    //Todo=> Check if it's only numbers
    const onlyNumbersRegex = /^\d+$/;
    if (onlyNumbersRegex.test(value.replace(/\s/g, ""))) {
      errors.numbers = "Sender ID cannot contain only numbers";
    }

    //Todo=> Check for duplicates (exclude the current editing sender)
    const isDuplicate =
      Array.isArray(senderIds) &&
      senderIds.some(
        (sender) => 
          sender.sender.toLowerCase() === value.toLowerCase() &&
          (excludeSlug ? sender.sender_slug !== excludeSlug : true)
      );
    if (isDuplicate) {
      errors.duplicate = "This Sender ID already exists";
    }

    return errors;
  };

  const handleAddSenderId = async () => {
    const validationErrors = validateSenderId(newSenderId);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsAddingSender(true);
      try {
        const response = await senderRequests.addSenderId(newSenderId);
        // const { data } = response;
        const data = response?.data
        const validationError =  response?.validationError || null;
        if(validationError) {
          toast.error(validationError);
          return;
        }
  
        if (data.data.status_code === "000" && !data.data.in_error) {
          // Refresh the sender IDs with current pagination
          const response = await senderRequests.getSenders(
            currentPage,
            pagination.perPage,
            searchQuery
          );
          const { data: senderData } = response;
          if (
            senderData.data.status_code === "000" &&
            !senderData.data.in_error
          ) {
            const apiData = senderData?.data?.data;
            const senderList = apiData?.data || [];
            const paginationData = apiData?.pagination || {};
            const stats = apiData?.sender_id_stats || {};

            setSenderIds(Array.isArray(senderList) ? senderList : []);
            setStatsData(stats);
            setPagination({
              currentPage: paginationData.current_page || currentPage,
              perPage: paginationData.per_page || pagination.perPage,
              total: paginationData.total || 0,
              lastPage: paginationData.last_page || 1,
              from: paginationData.from || 0,
              to: paginationData.to || 0,
            });
          }
          setNewSenderId("");
          setShowAddForm(false);
          setErrors({});
          toast.success("Sender ID added successfully!");
        } else {
          toast.error(data.data.reason);
        }
      } catch (error) {
        console.error('Error adding sender ID:', error);
        toast.error("An error occurred while adding the sender ID");
      } finally {
        setIsAddingSender(false);
      }
    }
  };

  const handleEditSenderId = async (id) => {
    const validationErrors = validateSenderId(editValue, id);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setIsEditingSender(true);
      try {
        const response = await senderRequests.updateSenderId(id, editValue);
        const { data } = response;
        if (data.data.status_code === "000" && !data.data.in_error) {
          // Refresh the sender IDs with current pagination
          const response = await senderRequests.getSenders(
            currentPage,
            pagination.perPage,
            searchQuery
          );
          const { data: senderData } = response;
          if (
            senderData.data.status_code === "000" &&
            !senderData.data.in_error
          ) {
            const apiData = senderData?.data?.data;
            const senderList = apiData?.data || [];
            const paginationData = apiData?.pagination || {};
            const stats = apiData?.sender_id_stats || {};

            setSenderIds(Array.isArray(senderList) ? senderList : []);
            setStatsData(stats);
            setPagination({
              currentPage: paginationData.current_page || currentPage,
              perPage: paginationData.per_page || pagination.perPage,
              total: paginationData.total || 0,
              lastPage: paginationData.last_page || 1,
              from: paginationData.from || 0,
              to: paginationData.to || 0,
            });
          }
          setEditingId(null);
          setEditValue("");
          setErrors({});
          toast.success("Sender ID updated successfully!");
        } else {
          toast.error(data.data.reason);
        }
      } catch (error) {
        console.error('Error updating sender ID:', error);
        toast.error("An error occurred while updating the sender ID");
      } finally {
        setIsEditingSender(false);
      }
    }
  };

  const handleDeleteSenderId = async () => {
    if (!senderToDelete) return;

    setIsDeletingSender(true);
    try {
      const response = await senderRequests.deleteSenderId(
        senderToDelete.sender_slug || senderToDelete.id
      );
      const { data } = response;
      // //console.log("data", data);
      if (data.data.status_code === "000" && !data.data.in_error) {
        // Refresh the sender IDs with current pagination
        const response = await senderRequests.getSenders(
          currentPage,
          pagination.perPage,
          searchQuery
        );
        const { data: senderData } = response;
        if (
          senderData.data.status_code === "000" &&
          !senderData.data.in_error
        ) {
          const apiData = senderData?.data?.data;
          const senderList = apiData?.data || [];
          const paginationData = apiData?.pagination || {};
          const stats = apiData?.sender_id_stats || {};

          setSenderIds(Array.isArray(senderList) ? senderList : []);
          setStatsData(stats);
          setPagination({
            currentPage: paginationData.current_page || currentPage,
            perPage: paginationData.per_page || pagination.perPage,
            total: paginationData.total || 0,
            lastPage: paginationData.last_page || 1,
            from: paginationData.from || 0,
            to: paginationData.to || 0,
          });
        }
        toast.success("Sender ID deleted successfully!");
        setShowDeleteModal(false);
        setSenderToDelete(null);
      } else {
        toast.error(data.data.reason);
      }
    } catch (error) {
      console.error('Error deleting sender ID:', error);
      toast.error("An error occurred while deleting the sender ID");
    } finally {
      setIsDeletingSender(false);
    }
  };

  const openDeleteModal = (sender) => {
    setSenderToDelete(sender);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSenderToDelete(null);
  };

  const startEditing = (sender) => {
    // //console.log('Starting edit for:', sender);
    setEditingId(sender.sender_slug || sender.id);
    setEditValue(sender.sender);
    setErrors({});
  };

  const cancelEditing = () => {
    // //console.log('Canceling edit');
    setEditingId(null);
    setEditValue("");
    setErrors({});
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-50 text-green-700 border-green-200";
      case "pending":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "rejected":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Sender ID copied to clipboard!");
  };

  // Handle per page change
  const handlePerPageChange = (perPage) => {
    setPagination(prev => ({
      ...prev,
      perPage: parseInt(perPage),
      currentPage: 1
    }));
    setCurrentPage(1);
  };

  // Get current page sender IDs for table view
  const currentSenderIds = senderIds;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <CustomerLayout>
      <div className="pt-20 pb-8 min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 sm:pt-24 sm:pb-12">
        <div className="px-3 mx-auto max-w-8xl sm:px-4 md:px-6 lg:px-8">
          {/* Header Section */}
          <SenderHeader senderIds={senderIds} />

          {/* Stats Cards - Pass statsData */}
          <StatsCards statsData={statsData} />

          {/* View Toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
              <button
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'card'
                    ? 'bg-blue-50 text-blue-600 border border-blue-100'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'table'
                    ? 'bg-blue-50 text-blue-600 border border-blue-100'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <SenderManagementContent
            senderIds={senderIds}
            currentSenderIds={currentSenderIds}
            currentPage={pagination.currentPage}
            totalPages={pagination.lastPage}
            startIndex={pagination.from}
            endIndex={pagination.to}
            itemsPerPage={pagination.perPage}
            handlePageChange={handlePageChange}
            searchQuery={searchInput}
            setSearchQuery={handleSearchChange}
            handleClearSearch={handleClearSearch}
            showTips={showTips}
            setShowTips={setShowTips}
            showAddForm={showAddForm}
            setShowAddForm={setShowAddForm}
            newSenderId={newSenderId}
            setNewSenderId={setNewSenderId}
            errors={errors}
            setErrors={setErrors}
            handleAddSenderId={handleAddSenderId}
            isAddingSender={isAddingSender}
            editingId={editingId}
            editValue={editValue}
            setEditValue={setEditValue}
            handleEditSenderId={handleEditSenderId}
            cancelEditing={cancelEditing}
            startEditing={startEditing}
            copyToClipboard={copyToClipboard}
            openDeleteModal={openDeleteModal}
            showDeleteModal={showDeleteModal}
            closeDeleteModal={closeDeleteModal}
            senderToDelete={senderToDelete}
            handleDeleteSenderId={handleDeleteSenderId}
            isDeletingSender={isDeletingSender}
            getStatusColor={getStatusColor}
            isEditingSender={isEditingSender}
            viewMode={viewMode}
            pagination={pagination}
            handlePerPageChange={handlePerPageChange}
          />

          {/* Help Section */}
          <HelpSection />
        </div>
      </div>

      {/* Modals - positioned outside the card container */}
      <AddSenderModal
        showAddForm={showAddForm}
        setShowAddForm={setShowAddForm}
        newSenderId={newSenderId}
        setNewSenderId={setNewSenderId}
        errors={errors}
        setErrors={setErrors}
        handleAddSenderId={handleAddSenderId}
        isAddingSender={isAddingSender}
      />

      <DeleteConfirmModal
        showDeleteModal={showDeleteModal}
        closeDeleteModal={closeDeleteModal}
        senderToDelete={senderToDelete}
        handleDeleteSenderId={handleDeleteSenderId}
        isDeletingSender={isDeletingSender}
      />
    </CustomerLayout>
  );
};

export default Senders;